import { useMemo } from "react";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";

export type StaffFilterOption = { label: string; value: string };

/**
 * The current user as a staff filter option, for seeding a locked staff filter.
 *
 * The filter state stores `{ label, value }` options (the list hooks read `.value` off them), so a
 * pinned filter needs the same shape — an id alone would not survive into the request params.
 * Built from the already-loaded current user rather than a staff lookup, so seeding doesn't wait
 * on a request. Returns `null` when no lock applies or the user isn't loaded yet.
 */
export default function useStaffOptionForLock(
  lockedStaffId?: string
): StaffFilterOption | null {
  const currentUser = useAppSelector(selectCurrentUser);

  return useMemo(() => {
    if (!lockedStaffId || !currentUser?.id) return null;
    if (String(currentUser.id) !== lockedStaffId) return null;

    const label =
      currentUser.full_name ||
      [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" ");

    return { label, value: lockedStaffId };
  }, [
    lockedStaffId,
    currentUser?.id,
    currentUser?.full_name,
    currentUser?.first_name,
    currentUser?.last_name
  ]);
}
